import { useMemo, useState } from "react";
import { Calculator, Info } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const formatIDR = (n) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

export default function KprCalculator() {
  const [harga, setHarga] = useState("");
  const [dp, setDp] = useState("");
  const [tenor, setTenor] = useState(15);
  const [bunga, setBunga] = useState("");

  const result = useMemo(() => {
    const h = parseFloat(harga) || 0;
    const d = parseFloat(dp) || 0;
    const r = (parseFloat(bunga) || 0) / 100 / 12;
    const n = (parseFloat(tenor) || 0) * 12;
    const pinjaman = Math.max(h - d, 0);
    if (!pinjaman || !n) return { pinjaman, cicilan: 0 };
    const cicilan = r > 0 ? (pinjaman * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : pinjaman / n;
    return { pinjaman, cicilan };
  }, [harga, dp, tenor, bunga]);

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand";

  return (
    <section className="bg-mist-blue bg-grid-blue py-24" data-testid="kpr-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHeading
          number="09"
          eyebrow="Simulasi KPR"
          title="Simulasi KPR Rumah Pontianak"
          description="Hitung estimasi pinjaman dan cicilan bulanan rumah Type 36 impian Anda."
        />
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-3xl border border-slate-100 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-9">
              <div className="space-y-5">
                <div>
                  <label htmlFor="kpr-harga" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Harga Rumah (Rp)</label>
                  <input id="kpr-harga" data-testid="kpr-input-harga" type="number" min="0" value={harga} onChange={(e) => setHarga(e.target.value)} placeholder="Contoh: 350000000" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="kpr-dp" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Uang Muka / DP (Rp)</label>
                  <input id="kpr-dp" data-testid="kpr-input-dp" type="number" min="0" value={dp} onChange={(e) => setDp(e.target.value)} placeholder="Contoh: 50000000" className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="kpr-tenor" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Tenor (Tahun)</label>
                    <select id="kpr-tenor" data-testid="kpr-input-tenor" value={tenor} onChange={(e) => setTenor(e.target.value)} className={inputCls}>
                      {[5, 10, 15, 20, 25, 30].map((t) => (
                        <option key={t} value={t}>{t} tahun</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="kpr-bunga" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Suku Bunga (% / tahun)</label>
                    <input id="kpr-bunga" data-testid="kpr-input-bunga" type="number" min="0" step="0.01" value={bunga} onChange={(e) => setBunga(e.target.value)} placeholder="Contoh: 6.5" className={inputCls} />
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col rounded-3xl bg-brand-dark p-7 sm:p-9">
              <div className="flex items-center gap-3">
                <Calculator className="h-5 w-5 text-brand-sky" />
                <h3 className="font-display text-lg font-bold text-white">Hasil Estimasi</h3>
              </div>
              <div className="mt-6 grid flex-1 gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="kpr-output-pinjaman">
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400">Estimasi Pinjaman</p>
                  <p className="mt-2 font-display text-xl font-bold text-white sm:text-2xl">{formatIDR(result.pinjaman)}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="kpr-output-cicilan">
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400">Estimasi Cicilan / Bulan</p>
                  <p className="mt-2 font-display text-xl font-bold text-brand-sky sm:text-2xl">{formatIDR(result.cicilan)}</p>
                </div>
              </div>
              <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-slate-400" data-testid="kpr-disclaimer">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                Hasil simulasi hanya merupakan estimasi dan bukan penawaran kredit resmi dari bank.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
